/**
 * The operator's atypical-airspace (ARC-a) declaration of a mission: shows the stored
 * atypical_airspace_declaration with its evaluation (the atypical_declaration_* provisional reasons of the
 * SORA assessment), and declares, changes or removes it through PUT/DELETE
 * /missions/{id}/atypical_airspace_declaration. The server checks reasons a), c) and d) over the ŘLP zones
 * and refuses an invalid declaration with 400 atypical_declaration_invalid; the authority assesses it.
 * The zone of a) is chosen by the server, not here.
 */
import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Pencil, Plane, Save, Trash2, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import api from "../../lib/api";
import { getApiErrorMessage } from "../../lib/apiErrors";
import { localizeReasonMessage } from "../../lib/provisionalReasons";
import { fillTemplate } from "../../lib/arcDisplay";
import {
  ATYPICAL_NOTE_MAX_LENGTH, ATYPICAL_REASONS, atypicalDeclarationPayload, atypicalDeclarationProblem,
  isAtypicalNoteRequired,
} from "../../lib/atypicalDeclaration";

export const AtypicalAirspaceDeclarationCard = ({
  mission,
  onUpdate,
  evaluationReasons = [],
  currentUserId,
  language = 'cs',
  t = {},
}) => {
  const declaration = mission?.atypical_airspace_declaration || null;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState(declaration?.reason || "");
  const [note, setNote] = useState(declaration?.note || "");

  // A refreshed mission (after a save elsewhere or a conflict) replaces the form values unless the user is editing.
  useEffect(() => {
    if (!editing) {
      setReason(declaration?.reason || "");
      setNote(declaration?.note || "");
    }
  }, [declaration?.reason, declaration?.note, editing]);

  const problem = atypicalDeclarationProblem(reason, note);
  const evaluation = (evaluationReasons || []).filter(r => typeof r.code === 'string' && r.code.startsWith('atypical_declaration_'));
  const reasonLabel = (value) => t.reasons?.[value] || value;

  const handleError = (error, fallback) => {
    if (error?.response?.status === 409) {
      toast.error(t.conflict);
      setEditing(false);
      if (onUpdate) onUpdate();
      return;
    }
    toast.error(getApiErrorMessage(error, language, fallback));
  };

  const handleSave = async () => {
    if (!mission?.id || problem) return;
    setBusy(true);
    try {
      await api.put(`/missions/${mission.id}/atypical_airspace_declaration`, atypicalDeclarationPayload(reason, note));
      toast.success(t.saved);
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      handleError(error, t.saveError);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!mission?.id || !window.confirm(t.removeConfirm)) return;
    setBusy(true);
    try {
      await api.delete(`/missions/${mission.id}/atypical_airspace_declaration`);
      toast.success(t.removed);
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      handleError(error, t.removeError);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    setReason(declaration?.reason || "");
    setNote(declaration?.note || "");
    setEditing(false);
  };

  const startEditing = (e) => {
    e.stopPropagation();
    setExpanded(true);
    setEditing(true);
  };

  const declaredBy = !declaration?.declared_by
    ? null
    : declaration.declared_by === currentUserId
    ? t.declaredByYou
    : fillTemplate(t.declaredByOther, { id: String(declaration.declared_by).slice(0, 8) });

  return (
    <Card className="overflow-hidden" data-testid="atypical-declaration-card">
      <CardHeader
        className="cursor-pointer hover:bg-[var(--bg-elevated)] transition-colors py-3 px-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Plane className="h-5 w-5 flex-shrink-0 text-[var(--text-muted)]" />
            <CardTitle className="text-base font-medium">{t.title}</CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {expanded && !editing && (
              <Button variant="ghost" size="sm" onClick={startEditing} disabled={busy} data-testid="atypical-declaration-edit">
                <Pencil className="h-4 w-4 mr-1" />
                {declaration ? t.edit : t.declare}
              </Button>
            )}
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-[var(--text-muted)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--text-muted)]" />
            )}
          </div>
        </div>
        {!expanded && (
          <p className="text-sm text-[var(--text-muted)] mt-1" data-testid="atypical-declaration-summary">
            {declaration ? reasonLabel(declaration.reason) : t.notDeclared}
          </p>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 px-4 pb-4 space-y-4">
          <p className="text-sm text-[var(--text-muted)]">{t.subtitle}</p>

          {!editing && (
            declaration ? (
              <div className="p-4 rounded-lg border border-[var(--border-default)] bg-[var(--bg-elevated)] space-y-2" data-testid="atypical-declaration-stored">
                <p className="text-sm">
                  <span className="font-medium">{t.reasonLabel}:</span> {reasonLabel(declaration.reason)}
                </p>
                {declaration.note && (
                  <p className="text-sm whitespace-pre-wrap">
                    <span className="font-medium">{t.noteLabel}:</span> {declaration.note}
                  </p>
                )}
                <p className="text-xs text-[var(--text-muted)]">
                  {t.declaredAt}: {new Date(declaration.declared_at).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-GB')}
                  {declaredBy && <> · {t.declaredBy}: {declaredBy}</>}
                </p>
                {evaluation.length > 0 && (
                  <div className="pt-1 space-y-1" data-testid="atypical-declaration-evaluation">
                    <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t.evaluation}</p>
                    {evaluation.map((r, idx) => (
                      <p key={r.code || idx} className="text-sm text-[var(--text-secondary)]">{localizeReasonMessage(r, language)}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-[var(--text-muted)]">{t.notDeclared}</p>
            )
          )}

          {editing && (
            <div className="space-y-4">
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium mb-1">
                  {t.reasonLabel}<span className="text-red-600 ml-0.5">*</span>
                </legend>
                {ATYPICAL_REASONS.map((value) => (
                  <label
                    key={value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                      reason === value
                        ? 'border-[var(--brand-accent)] bg-[var(--bg-elevated)]'
                        : 'border-[var(--border-default)]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="atypical-declaration-reason"
                      value={value}
                      checked={reason === value}
                      onChange={() => setReason(value)}
                      disabled={busy}
                      className="mt-1"
                      data-testid={`atypical-reason-${value}`}
                    />
                    <span className="space-y-0.5">
                      <span className="block text-sm font-medium">{reasonLabel(value)}</span>
                      <span className="block text-xs text-[var(--text-muted)]">{t.reasonHints?.[value]}</span>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="space-y-2">
                <Label htmlFor="atypical-declaration-note">
                  {t.noteLabel}
                  {isAtypicalNoteRequired(reason) && reason && <span className="text-red-600 ml-0.5">*</span>}
                </Label>
                <Textarea
                  id="atypical-declaration-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t.notePlaceholder}
                  disabled={busy}
                  rows={3}
                  aria-invalid={problem === 'noteRequired' || problem === 'noteTooLong'}
                  data-testid="atypical-declaration-note"
                />
                {problem && problem !== 'reasonRequired' && (
                  <p className="text-xs text-red-600" data-testid="atypical-declaration-problem">
                    {problem === 'noteTooLong'
                      ? fillTemplate(t.noteTooLong, { max: ATYPICAL_NOTE_MAX_LENGTH })
                      : t.noteRequired}
                  </p>
                )}
                {problem === 'reasonRequired' && (
                  <p className="text-xs text-[var(--text-muted)]">{t.reasonRequired}</p>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-2">
                {declaration && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemove}
                    disabled={busy}
                    className="mr-auto text-red-600"
                    data-testid="atypical-declaration-remove"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {t.remove}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleCancel} disabled={busy}>
                  <X className="h-4 w-4 mr-1" />
                  {t.cancel}
                </Button>
                <Button size="sm" onClick={handleSave} disabled={busy || !!problem} data-testid="atypical-declaration-save">
                  <Save className="h-4 w-4 mr-1" />
                  {busy ? t.saving : t.save}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
